import React, { useEffect, useState } from "react";
import "./ShiftAssigns.css";
import { shiftListAssign, shiftsAssignmentSubmit, shiftsGets, getDesignationsForEmployees } from "../../../services/shiftsService";
import { format } from "date-fns";
import { getAuditLog } from "../../../utils/calculateDaysInMonthUtils";
import { formatTime, convertToIST } from "../../../utils/validations";
import Select from "react-select";
import { getDistrictByZoneId } from "../../../services/monthlyAttendanceService";

// Helper function to safely format dates
const safeFormatDate = (dateValue, outputFormat = "dd-MM-yyyy", defaultValue = "-") => {
  if (!dateValue) return defaultValue;
  try {
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return defaultValue;
    return format(date, outputFormat);
  } catch (error) {
    console.error("Date formatting error:", error);
    return defaultValue;
  }
};

// Helper function to get date string for input fields
const getDateForInput = (dateValue) => {
  if (!dateValue) return "";
  try {
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return "";
    return format(date, "yyyy-MM-dd");
  } catch (error) {
    console.error("Date input formatting error:", error);
    return "";
  }
};

const ShiftAssigns = () => {
  const [employeeList, setEmployeeList] = useState([]);
  const [shiftList, setShiftList] = useState([]);
  const [editingRows, setEditingRows] = useState(new Set());
  const [editedData, setEditedData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });

  const districtName = localStorage.getItem("districtName");
  const roleId = Number(localStorage.getItem("roleId"));
  const zoneId = localStorage.getItem("zoneId");
  const zoneName = localStorage.getItem("zoneName");
  const [designationOptions, setDesignationOptions] = useState([]);
  const [designation, setDesignation] = useState("");

  const [districtOptions, setDistrictOptions] = useState([]);
  const [district, setDistrict] = useState("");
  const [districtId, setDistrictId] = useState("");

  // Helper function to format time in IST
  const formatTimeInIST = (timeStr, dateStr) => {
    if (!timeStr || timeStr === "-" || timeStr === null) return "Not Assigned";
    // If we have a date string, use convertToIST
    if (dateStr) {
      try {
        return convertToIST(dateStr, timeStr);
      } catch (error) {
        console.error("Error converting time:", error);
        return "Invalid Time";
      }
    }
    // Fallback to original formatTime if no date is available
    return formatTime(timeStr);
  };

  const fetchShiftAssigns = async () => {
    try {
      const response = await shiftListAssign(districtId, designation);
      setEmployeeList(response.data);
    } catch (error) {
      console.error("Error fetching shifts:", error);
    }
  };

  const loadShifts = async () => {
    try {
      const res = await shiftsGets();
      setShiftList(res.data);
    } catch (err) {
      console.log("Error loading shifts", err);
    }
  };

  useEffect(() => {
    loadDesignations();
    loadShifts();
  }, []);

  const loadDesignations = async () => {
    try {
      const data = await getDesignationsForEmployees();
      const list = data.map(d => ({
        value: d.id,
        label: d.designationName,
      }));
      setDesignationOptions(list);
    } catch (error) {
      console.error("Error loading designations:", error);
    }
  };

  useEffect(() => {
    if (districtId) {
      fetchShiftAssigns();
    } else {
      setEmployeeList([]);
    }
  }, [districtId, designation]);

  const startEdit = (id) => {
    setEditingRows(prev => new Set(prev).add(id));

    if (!editedData[id]) {
      const emp = employeeList.find(e => e.id === id);
      if (emp) {
        setEditedData(prev => ({
          ...prev,
          [emp.postingId]: {
            shiftId: emp.shiftId || "",
            shiftStartDate: emp.shiftStartDate || "",
            shiftEndDate: emp.shiftEndDate || "",
          }
        }));
      }
    }
  };

  const handleInputChange = (selectedOption, field) => {
    const value = selectedOption ? selectedOption.value : "";
    switch (field) {
      case "district":
        if (value === "ALL") {
          setDistrict("ALL");
          setDistrictId("");
        } else {
          setDistrict(value);
          setDistrictId(value);
        }
        break;
      case "designation":
        setDesignation(value);
        break;
      default:
        break;
    }
  };

  const cancelEdit = (id) => {
    const emp = employeeList.find(e => e.id === id);
    if (!emp) return;
    
    setEditingRows(prev => {
      const updated = new Set(prev);
      updated.delete(id);
      return updated;
    });
    
    setEditedData(prev => {
      const updated = { ...prev };
      delete updated[emp.postingId];
      return updated;
    });
  };

  const handleChange = (id, field, value) => {
    const emp = employeeList.find(e => e.id === id);
    if (!emp) return;

    setEditedData(prev => ({
      ...prev,
      [emp.postingId]: {
        ...prev[emp.postingId],
        [field]: value
      }
    }));
  };

  const hasChanges = Object.keys(editedData).length > 0;

  const handleBulkSubmit = async () => {
    if (!hasChanges) {
      setMessage({ text: "No changes to submit", type: "info" });
      return;
    }

    setIsSubmitting(true);
    setMessage({ text: "", type: "" });

    const list = Object.keys(editedData).map(postingId => {
      const data = editedData[postingId];
      const emp = employeeList.find(e => e.postingId === parseInt(postingId));
      
      if (!emp) {
        console.error(`Employee not found for postingId: ${postingId}`);
        return null;
      }
      
      return {
        postingId: emp.postingId,
        shiftId: parseInt(data.shiftId),
        shiftStartDate: data.shiftStartDate,
        shiftEndDate: data.shiftEndDate
      };
    }).filter(item => item !== null);

    if (list.length === 0) {
      setMessage({ text: "No valid changes to submit", type: "error" });
      setIsSubmitting(false);
      return;
    }

    const payload = {
      auditLog: getAuditLog(),
      list,
    };

    try {
      const response = await shiftsAssignmentSubmit(payload);

      if (response.status === 200 || response.status === 201) {
        const updatedList = employeeList.map(emp => {
          if (editedData[emp.postingId]) {
            const updated = editedData[emp.postingId];
            const newShift = shiftList.find(s => s.shiftId === parseInt(updated.shiftId));
            return {
              ...emp,
              shiftId: parseInt(updated.shiftId),
              shiftStartDate: updated.shiftStartDate,
              shiftEndDate: updated.shiftEndDate,
              shiftName: newShift?.shiftName || emp.shiftName,
            };
          }
          return emp;
        });

        setEmployeeList(updatedList);
        setEditingRows(new Set());
        setEditedData({});
        setMessage({ text: "All changes submitted successfully!", type: "success" });
        setTimeout(() => setMessage({ text: "", type: "" }), 5000);
      }
    } catch (error) {
      console.error("Submit failed:", error);
      setMessage({ text: "Failed to submit changes. Please try again.", type: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (message.text) {
      const timer = setTimeout(() => {
        setMessage({ text: "", type: "" });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [message.text]);

  const handleClear = () => {
    setDesignation("");
    setDistrict('');
    setDistrictId('');
  };

  const getDistrictByZones = async () => {
    const data = await getDistrictByZoneId(zoneId);
    if (data?.districts) {
      const list = data.districts.map(d => ({
        value: d.districtId,
        label: d.districtName,
      }));
      setDistrictOptions(list);
    }
  };

  useEffect(() => {
    if (zoneId) {
      getDistrictByZones();
    }
  }, [zoneId]);

  return (
    <div className="shift-assign-container">
      <h2 className="page-title">
        Shift Assignment List - <span className="district-highlight">{zoneName}</span>
      </h2>
      <div className="d-flex justify-content-evenly">
        <div className="d-flex justify-content-center align-items-center mt-2 mb-1">
          <div className="mb-0"><label><span className=" text-danger">*</span>District : </label></div>
          <div style={{ minWidth: "200px" }}>
            <Select
              name="district"
              options={districtOptions}
              value={districtOptions.find(opt => opt.value === district) || null}
              onChange={option => handleInputChange(option, "district")}
              placeholder="Select District"
              isClearable
              className="w-100"
            />
          </div>
        </div>
        <div className="d-flex justify-content-center align-items-center mt-2 mb-1">
          <div className="mb-0"><label>Designation : </label></div>
          <div style={{ minWidth: "200px" }}>
            <Select
              name="designation"
              options={designationOptions}
              value={designationOptions.find(opt => opt.value === designation) || null}
              onChange={option => handleInputChange(option, "designation")}
              placeholder="Select Designation"
              isClearable
              className="w-100"
            />
          </div>
        </div>
      </div>
      <div className="text-end">
        <button className="btn btn-danger mb-3 clear-s-b-disg" onClick={() => { handleClear() }} disabled={!districtId}>
          Clear
        </button>
      </div>

      <div className="table-wrapper">
        <table className="shift-table-assign">
          <thead>
            <tr>
              <th>S.No</th>
              <th>Employee Name</th>
              <th>Designation</th>
              <th>Contact</th>
              <th>From Date</th>
              <th>To Date</th>
              <th>Shift</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {employeeList.length === 0 ? (
              <tr>
                <td colSpan="8" className="no-data">No Records Found</td>
              </tr>
            ) : (
              employeeList.map((emp, index) => {
                const isEditing = editingRows.has(emp.id);
                const currentData = editedData[emp.postingId] || emp;

                return (
                  <tr key={emp.id} className={isEditing ? "editing-row" : ""}>
                    <td>{index + 1}</td>
                    <td className="text-start">{emp.fullName}</td>
                    <td>{emp.designation}</td>
                    <td>{emp.contactNumber}</td>
                    
                    {/* From Date */}
                    <td>
                      {isEditing ? (
                        <input
                          type="date"
                          className="form-input"
                          value={getDateForInput(currentData.shiftStartDate)}
                          onChange={(e) => handleChange(emp.id, "shiftStartDate", e.target.value)}
                          min={new Date().toISOString().split("T")[0]}
                        />
                      ) : (
                        safeFormatDate(emp.shiftStartDate)
                      )}
                    </td>
                    
                    {/* To Date */}
                    <td>
                      {isEditing ? (
                        <input
                          type="date"
                          className="form-input"
                          value={getDateForInput(currentData.shiftEndDate)}
                          onChange={(e) => handleChange(emp.id, "shiftEndDate", e.target.value)}
                          min={getDateForInput(currentData.shiftStartDate) || new Date().toISOString().split("T")[0]}
                        />
                      ) : (
                        safeFormatDate(emp.shiftEndDate)
                      )}
                    </td>

                    {/* Shift with IST conversion */}
                    <td>
                      {isEditing ? (
                        <select
                          className="form-select"
                          value={currentData.shiftId || ""}
                          onChange={(e) => handleChange(emp.id, "shiftId", e.target.value)}
                        >
                          <option value="">Select Shift</option>
                          {shiftList.map(shift => (
                            <option key={shift.shiftId} value={shift.shiftId}>
                              {shift.shiftName} ({formatTimeInIST(shift.startTime, currentData.shiftStartDate)} - {formatTimeInIST(shift.endTime, currentData.shiftEndDate)})
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span className="shift-display">
                          {emp.shiftName && emp.shiftStartTime && emp.shiftEndTime && emp.shiftStartDate && emp.shiftEndDate
                            ? `${emp.shiftName}: ${formatTimeInIST(emp.shiftStartTime, emp.shiftStartDate)} - ${formatTimeInIST(emp.shiftEndTime, emp.shiftEndDate)}`
                            : "Not Assigned"}
                        </span>
                      )}
                    </td>

                    {/* Action Buttons */}
                    <td className="action-buttons">
                      {isEditing ? (
                        <button onClick={() => cancelEdit(emp.id)} className="btn-cancel">
                          Cancel
                        </button>
                      ) : (
                        <button onClick={() => startEdit(emp.id)} className="btn-edit">
                          Edit
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      
      {message.text && (
        <div className="confirm-overlay">
          <div className="success-message-popup">{message.text}</div>
        </div>
      )}

      <div className="bulk-submit-footer">
        <div className="change-summary">
          You have modified {Object.keys(editedData).length} employee(s)
        </div>
        <button
          onClick={handleBulkSubmit}
          disabled={editingRows.size === 0 || isSubmitting}
          className="btn-submit-all"
        >
          {isSubmitting ? "Submitting..." : `Submit All Changes (${Object.keys(editedData).length})`}
        </button>
      </div>
    </div>
  );
};

export default ShiftAssigns;